import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Briefcase,
  Building2,
  LogOut,
  Mail,
  MapPin,
  ShieldCheck,
} from "lucide-react";
import type { ProfileState } from "../hooks/useProfile";

type Props = {
  open: boolean;
  anchor: HTMLElement | null;
  state: ProfileState;
  /** Fallback display name from MSAL account (used while /me is loading). */
  fallbackName: string;
  fallbackEmail: string;
  fallbackInitials: string;
  /** When true, an "Admin" link is rendered above Sign out. */
  isAdmin?: boolean;
  onClose: () => void;
  onSignOut: () => void;
};

function pickPosition(anchor: HTMLElement | null) {
  if (!anchor) return { top: 0, left: 0 };
  const rect = anchor.getBoundingClientRect();
  const width = 300;
  // Anchor below the chip, right-aligned to the chip's right edge so it
  // doesn't slide off-screen when the header is narrow.
  const left = Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12));
  return { top: rect.bottom + 8, left };
}

export function ProfilePopover({
  open,
  anchor,
  state,
  fallbackName,
  fallbackEmail,
  fallbackInitials,
  isAdmin = false,
  onClose,
  onSignOut,
}: Props) {
  const popRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t)) return;
      if (anchor?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, anchor, onClose]);

  if (!open) return null;
  const { top, left } = pickPosition(anchor);

  const p = state.profile;
  const name = p?.displayName || fallbackName;
  const email = p?.mail || p?.userPrincipalName || fallbackEmail;
  const title = p?.jobTitle;
  const department = p?.department;
  const office = p?.officeLocation;

  // Rendered through a portal because the app header has `backdrop-filter`,
  // which makes it the containing block for any `position: fixed` descendant.
  // Without the portal, our viewport-coordinate top/left would be reinterpreted
  // as offsets from the header and the popover would shoot off the right edge.
  return createPortal(
    <div
      ref={popRef}
      className="profile-popover"
      style={{ top, left }}
      role="dialog"
      aria-label="Account"
    >
      <div className="profile-popover-head">
        <div className="profile-avatar-lg" aria-hidden>
          {state.photoUrl ? (
            <img src={state.photoUrl} alt="" />
          ) : (
            <span>{fallbackInitials}</span>
          )}
        </div>
        <div className="profile-popover-id">
          <div className="profile-popover-name">{name}</div>
          {email ? <div className="profile-popover-email">{email}</div> : null}
        </div>
      </div>

      {state.loading ? (
        <div className="profile-popover-body profile-popover-loading">
          Loading profile…
        </div>
      ) : (
        <div className="profile-popover-body">
          {title ? (
            <div className="profile-popover-row">
              <Briefcase size={14} aria-hidden />
              <span>{title}</span>
            </div>
          ) : null}
          {department ? (
            <div className="profile-popover-row">
              <Building2 size={14} aria-hidden />
              <span>{department}</span>
            </div>
          ) : null}
          {office ? (
            <div className="profile-popover-row">
              <MapPin size={14} aria-hidden />
              <span>{office}</span>
            </div>
          ) : null}
          {!title && !department && !office && email ? (
            <div className="profile-popover-row">
              <Mail size={14} aria-hidden />
              <span>{email}</span>
            </div>
          ) : null}
        </div>
      )}

      <div className="profile-popover-foot">
        {isAdmin ? (
          <button
            type="button"
            className="profile-popover-link"
            onClick={() => {
              onClose();
              navigate("/admin");
            }}
          >
            <ShieldCheck size={15} aria-hidden />
            <span>Admin</span>
          </button>
        ) : null}
        <button
          type="button"
          className="profile-popover-signout"
          onClick={() => {
            onClose();
            onSignOut();
          }}
        >
          <LogOut size={15} aria-hidden />
          <span>Sign out</span>
        </button>
      </div>
    </div>,
    document.body
  );
}
